import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Physician Notification subsection adapter
 * Handles physician notification thresholds with nested less/greater pairs (14 fields)
 * This handles vital sign thresholds that trigger physician notifications
 */
export class PhysicianNotificationAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Temperature thresholds
    const tempLess = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS
    )?.at(0)

    const tempGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER
    )?.at(0)

    // Pulse thresholds
    const pulseLess = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS
    )?.at(0)

    const pulseGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER
    )?.at(0)

    // Respirations thresholds
    const respirationsLess = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS
    )?.at(0)

    const respirationsGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER
    )?.at(0)

    // Blood pressure thresholds
    const bpLess = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS
    )?.at(0)

    const bpGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER
    )?.at(0)

    // Weight thresholds
    const weightLess = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS
    )?.at(0)

    const weightGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER
    )?.at(0)

    // EtCO2 saturation thresholds
    const etco2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS
    )?.at(0)

    const etco2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER
    )?.at(0)

    // O2 saturation thresholds
    const o2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS
    )?.at(0)

    const o2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER
    )?.at(0)

    return {
      [SocFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS]: tempLess,
      [SocFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER]: tempGreater,
      [SocFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS]: pulseLess,
      [SocFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER]: pulseGreater,
      [SocFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS]: respirationsLess,
      [SocFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER]: respirationsGreater,
      [SocFormField.PHYSICIAN_NOTIFICATION_BP_LESS]: bpLess,
      [SocFormField.PHYSICIAN_NOTIFICATION_BP_GREATER]: bpGreater,
      [SocFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS]: weightLess,
      [SocFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER]: weightGreater,
      [SocFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS]: etco2SaturationLess,
      [SocFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER]: etco2SaturationGreater,
      [SocFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS]: o2SaturationLess,
      [SocFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER]: o2SaturationGreater
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    return [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_BP_LESS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_BP_GREATER)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER,
        value: this.getFormDataField(SocFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER)
      }
    ]
  }
}
